/**
 * Picture sources, sized for the screen asking for them.
 *
 * Every remote image on this site is a Cloudinary URL that already carries
 * `f_auto,q_auto` in its transform slot, so Cloudinary picks the format and the
 * quality. What it cannot pick is the width: without one it sends the original,
 * which means a phone downloads a picture wide enough for a desktop. Adding
 * `w_<n>` to the transform and offering the set as a `srcset` lets the browser
 * take the narrowest file that still covers its layout slot.
 *
 * Anything that is not a Cloudinary URL, such as artwork bundled in `public/`,
 * is left exactly as it is: there is no service in front of it to resize it.
 */

/** A Cloudinary delivery URL, with the transform slot we can add a width to. */
const CLOUDINARY = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.*)$/;

/** Widths that cover a phone through a 2× desktop card without overshooting. */
export const CARD_WIDTHS = [320, 480, 640, 960, 1280] as const;

/** Wider set for the full-bleed hero images, which are the page's largest paint. */
export const HERO_WIDTHS = [640, 960, 1280, 1920, 2560] as const;

/** True when `srcSetFor` can do anything useful with this source. */
export const isResizable = (src: string): boolean => CLOUDINARY.test(src);

/**
 * Cloudinary's transform keys, so a folder that happens to contain an underscore
 * is not mistaken for a transform and a width spliced into the middle of a path.
 */
const TRANSFORM_KEYS = new Set([
    'a',
    'ar',
    'b',
    'bo',
    'c',
    'co',
    'd',
    'dpr',
    'e',
    'f',
    'fl',
    'g',
    'h',
    'l',
    'o',
    'q',
    'r',
    't',
    'u',
    'w',
    'x',
    'y',
    'z',
]);

/** True when a path segment is a transform list such as `f_auto,q_auto`. */
const isTransformSegment = (segment: string): boolean => {
    const parts = segment.split(',');
    return parts.length > 0 && parts.every((part) => TRANSFORM_KEYS.has(part.split('_')[0]) && part.includes('_'));
};

/**
 * `src` with `w_<width>` folded into its transform slot. A URL that already
 * names a width is left alone, so an explicit choice in the CMS still wins.
 */
/**
 * `c_limit` rides along with every width: it caps the picture at that width but
 * never enlarges it. Without it Cloudinary happily upscales, and a `w_1280` of a
 * picture that is only 900 wide comes back half again as heavy as the original.
 */
export function atWidth(src: string, width: number): string {
    const match = CLOUDINARY.exec(src);
    if (!match) return src;
    const [, prefix, rest] = match;
    const [head, ...tail] = rest.split('/');
    if (!isTransformSegment(head)) return `${prefix}c_limit,w_${width}/${rest}`;
    if (head.split(',').some((part) => part.startsWith('w_'))) return src;
    return `${prefix}${head},c_limit,w_${width}/${tail.join('/')}`;
}

/** A `srcset` across `widths`, or undefined when the source cannot be resized. */
export function srcSetFor(src: string, widths: readonly number[] = CARD_WIDTHS): string | undefined {
    if (!isResizable(src)) return undefined;
    return widths.map((width) => `${atWidth(src, width)} ${width}w`).join(', ');
}
